import React, { useCallback, useMemo } from 'react'
// @ts-ignore no types
import { debounce } from 'lodash'
// @ts-ignore no types
import { connect } from 'react-redux'
// @ts-ignore no types
import classnames from 'classnames/bind'
import { Text, Icon, Grid, Button } from '@adc/ui-adc'
import type { FeatureFlags } from '@adc/ui-adc'
import type { primitives } from '@adc/blueprint'
import AdvertisementInjection from 'common/components/advertisement/advertisementInjection'
import { Address } from 'common/components/address'
import { usePropertyContext } from '../../context/property'
import { useListingContext } from '../../context/listing'
import SectionPanel from '../../components/sectionPanel'
import { formatNumber } from '../../utils/formatters'
import { withModalHooks } from '../../hooks/useModalFeatures'
import { withNudgeHook } from '../../hooks/useNudgesFeatures'
import { toggleOptinModal } from '../../actions/modals'
import type { SaveIconFlag } from '../../types'
import AssetType from './assetType'
// @ts-ignore no types
import styles from './index.scss'

const cx = classnames.bind(styles)
const elmId = 'property_header'

interface PropertyHeaderProps {
  buyerViews?: number
  toggleSaveProperty: (saveData: any) => void
  featureFlags: FeatureFlags
  user: primitives.AuctionUser
  pushAuthQueueItem: () => void
  pushOnboardingQueueItem: () => void
  toggleAuthModal: () => void
  nudgeOptInModal?: () => void
}

const PropertyHeader = ({
  buyerViews = 0,
  featureFlags,
  pushAuthQueueItem,
  pushOnboardingQueueItem,
  toggleAuthModal,
  nudgeOptInModal = () => {},
}: PropertyHeaderProps) => {
  const { listing } = useListingContext()
  const { property, propertyActions } = usePropertyContext()
  const isPropertySaved = property.is_currently_saved

  const debounceOnSave = useCallback(
    debounce(async () => {
      await propertyActions?.toggleSaveProperty({
        listing,
        property,
        pushAuthQueueItem,
        toggleAuthModal,
        pushOnboardingQueueItem,
        nudgeOptInModal,
      })
    }, 500),
    [
      propertyActions,
      property,
      listing,
      pushAuthQueueItem,
      toggleAuthModal,
      pushOnboardingQueueItem,
      nudgeOptInModal,
    ]
  )

  const saveProperty = () => {
    // @ts-ignore TODO:
    debounceOnSave(property)
  }

  const bedBaths = useMemo(
    () =>
      [
        [listing.primary_property?.summary?.total_bedrooms, 'Beds'],
        [listing.primary_property?.summary?.total_bathrooms, 'Baths'],
        [
          formatNumber(listing.primary_property?.summary?.living_size),
          'Sq. Ft.',
        ],
      ].map(([val, unit]) => (
        <span className={cx('bed-baths-separation')} key={unit}>
          <span className={cx('value')}>{val || ' - '}</span>{' '}
          <span className={cx('unit')}>{unit}</span>
        </span>
      )),
    [
      listing.primary_property?.summary?.total_bedrooms,
      listing.primary_property?.summary?.total_bathrooms,
      listing.primary_property?.summary?.living_size,
    ]
  )

  const { test, icon } = (featureFlags[
    'fe.resi.ab.testing.pdp.save.icon'
  ] as SaveIconFlag) || {
    test: { version: '' },
    icon: { color: 'info' },
  }

  return (
    <SectionPanel
      compact
      collapsible={false}
      elmId='address'
      style={{ paddingTop: 0 }}
    >
      <AdvertisementInjection
        dataAdSpace={`PDP_TOP_${listing.auction?.is_online ? 'REO' : 'TPS'}`}
        className='u-text-center u-pb-2'
      />
      <div className='u-pb-1'>
        <Grid verticalAlign='middle'>
          <Grid.Row>
            <Grid.Column computer={10} tablet={8} mobile={16}>
              <AssetType listing={listing} showTutorial />
              <Text.Header
                as='h1'
                level={1}
                color='grayDark'
                // @ts-ignore fix types in DS, export enum if possible
                weight='bold'
                compact
                data-elm-id={`${elmId}_address`}
              >
                {Address.toLines(listing)[0]}
                <div className={cx('spacer')} />
                <Text.Header
                  as='span'
                  level={3}
                  color='gray'
                  // @ts-ignore fix types in DS, export enum if possible
                  weight='normal'
                  compact
                  data-elm-id={`${elmId}_location`}
                >
                  {Address.toLines(listing)[1]}
                </Text.Header>
              </Text.Header>
            </Grid.Column>
            <Grid.Column computer={6} tablet={8} mobile={16}>
              <div
                className={cx(
                  'u-display-f',
                  'u-display-f-vcenter',
                  'u-text-center',
                  'u-display-f-end'
                )}
              >
                {buyerViews >= 10 && (
                  <Text
                    data-elm-id={`${elmId}_views`}
                    className={cx('u-nowrap')}
                  >
                    <Icon name='user outline' />
                    {formatNumber(buyerViews)} Views
                  </Text>
                )}
                <Button
                  className={cx(
                    'no-border-button',
                    'save-button',
                    test?.version
                  )}
                  role='secondary'
                  tag='a'
                  onClick={saveProperty}
                  elmId={`${elmId}_save_button`}
                >
                  <Icon
                    name='heart'
                    iconStyle={isPropertySaved ? 'solid' : 'regular'}
                    color={String(icon?.color) || 'info'}
                  />{' '}
                  {isPropertySaved ? 'Saved' : 'Save'}
                </Button>
              </div>
              {bedBaths && (
                <div className={cx('u-text-right')}>
                  <Text>{bedBaths}</Text>
                </div>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </SectionPanel>
  )
}

const mapStateToProps = (store: any) => ({
  buyerViews: store.propertyAnalytics.propertyViews,
  user: store.user,
  featureFlags: store.featureFlags,
})

const mapDispatchToProps = (dispatch: any) => ({
  openOptInModal: () => dispatch(toggleOptinModal(true)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withNudgeHook(withModalHooks(PropertyHeader)))
